import React from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit2, Trash2, History, Upload, Download, Search, X, 
  IndianRupee, Percent, Car, Calendar, FileText
} from 'lucide-react';
import { useUploadGuard } from '@/components/UploadGuard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const initialFormData = {
  brand_name: '',
  model: '',
  variant: '',
  color: '',
  ex_showroom_price: '',
  cgst_percent: '9',
  sgst_percent: '9',
  comprehensive_insurance: '',
  b2b: '',
  bpp_battery_protection: '',
  rsa_road_side_assistance: '',
  mandatory_accessories: '',
  registration_expenses: '',
  handling_incidental_expenses: '',
  pme_drive: '',
  price_benefits: '',
  discounts: '',
  baas: '',
  effective_from: new Date().toISOString().split('T')[0],
  effective_to: '',
  remarks: ''
};

function VehiclePriceMaster({ user, onLogout }) {
  const [prices, setPrices] = React.useState([]);
  const { ensureCompanyId, guardModal } = useUploadGuard(user);
  const [loading, setLoading] = React.useState(true);
  const [showModal, setShowModal] = React.useState(false);
  const [showHistoryModal, setShowHistoryModal] = React.useState(false);
  const [editingPrice, setEditingPrice] = React.useState(null);
  const [formData, setFormData] = React.useState(initialFormData);
  const [priceHistory, setPriceHistory] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  
  // Masters data
  const [brands, setBrands] = React.useState([]);
  const [models, setModels] = React.useState([]);
  const [variants, setVariants] = React.useState([]);
  const [colors, setColors] = React.useState([]);
  const [mastersLoading, setMastersLoading] = React.useState(true);

  React.useEffect(() => {
    fetchPrices();
    fetchMasters();
  }, []);

  const fetchPrices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/masters/vehicle-prices`, getAuthHeader());
      setPrices(response.data);
    } catch (error) {
      toast.error('Failed to fetch vehicle prices');
    }
    setLoading(false);
  };

  const fetchMasters = async () => {
    setMastersLoading(true);
    try {
      // Use vehicle-hierarchy endpoint for better structured data
      const response = await axios.get(`${BACKEND_URL}/api/masters/vehicle-hierarchy`, getAuthHeader());
      const data = response.data;
      
      // Deduplicate by name to avoid showing duplicates
      const dedupeByName = (arr) => {
        const seen = new Set();
        return (arr || []).filter(item => {
          if (seen.has(item.name)) return false;
          seen.add(item.name);
          return true;
        });
      };
      
      setBrands(dedupeByName(data.brands));
      setModels(dedupeByName(data.models));
      setVariants(dedupeByName(data.variants));
      setColors(dedupeByName(data.colors));
    } catch (error) {
      console.error('Failed to fetch masters from hierarchy:', error);
      toast.error('Failed to load vehicle masters data');
    }
    setMastersLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateTotals = () => {
    const ex = parseFloat(formData.ex_showroom_price) || 0;
    const cgstPct = parseFloat(formData.cgst_percent) || 0;
    const sgstPct = parseFloat(formData.sgst_percent) || 0;
    
    const cgstAmt = (ex * cgstPct) / 100;
    const sgstAmt = (ex * sgstPct) / 100;
    const totalGst = cgstAmt + sgstAmt;
    
    const insurance = parseFloat(formData.comprehensive_insurance) || 0;
    const b2b = parseFloat(formData.b2b) || 0;
    const bpp = parseFloat(formData.bpp_battery_protection) || 0;
    const rsa = parseFloat(formData.rsa_road_side_assistance) || 0;
    const accessories = parseFloat(formData.mandatory_accessories) || 0;
    const registration = parseFloat(formData.registration_expenses) || 0;
    const handling = parseFloat(formData.handling_incidental_expenses) || 0;
    const pme = parseFloat(formData.pme_drive) || 0;
    const benefits = parseFloat(formData.price_benefits) || 0;
    const discount = parseFloat(formData.discounts) || 0;
    const baas = parseFloat(formData.baas) || 0;
    
    const onRoad = ex + totalGst + insurance + b2b + bpp + rsa + accessories + 
                   registration + handling + pme + baas - benefits - discount;
    
    return { cgstAmt, sgstAmt, totalGst, onRoad };
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingPrice(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (price) => {
    setEditingPrice(price);
    setFormData({
      brand_name: price.brand_name || '',
      model: price.model || '',
      variant: price.variant || '',
      color: price.color || '',
      ex_showroom_price: price.ex_showroom_price || '',
      cgst_percent: price.cgst_percent || '9',
      sgst_percent: price.sgst_percent || '9',
      comprehensive_insurance: price.comprehensive_insurance || '',
      b2b: price.b2b || '',
      bpp_battery_protection: price.bpp_battery_protection || '',
      rsa_road_side_assistance: price.rsa_road_side_assistance || '',
      mandatory_accessories: price.mandatory_accessories || '',
      registration_expenses: price.registration_expenses || '',
      handling_incidental_expenses: price.handling_incidental_expenses || '',
      pme_drive: price.pme_drive || '',
      price_benefits: price.price_benefits || '',
      discounts: price.discounts || '',
      baas: price.baas || '',
      effective_from: price.effective_from || '',
      effective_to: price.effective_to || '',
      remarks: price.remarks || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.brand_name || !formData.model || !formData.variant) {
      toast.error('Brand, Model, and Variant are required');
      return;
    }
    
    if (!formData.ex_showroom_price) {
      toast.error('Ex-showroom price is required');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        ex_showroom_price: parseFloat(formData.ex_showroom_price) || 0,
        cgst_percent: parseFloat(formData.cgst_percent) || 0,
        sgst_percent: parseFloat(formData.sgst_percent) || 0,
        comprehensive_insurance: parseFloat(formData.comprehensive_insurance) || 0,
        b2b: parseFloat(formData.b2b) || 0,
        bpp_battery_protection: parseFloat(formData.bpp_battery_protection) || 0,
        rsa_road_side_assistance: parseFloat(formData.rsa_road_side_assistance) || 0,
        mandatory_accessories: parseFloat(formData.mandatory_accessories) || 0,
        registration_expenses: parseFloat(formData.registration_expenses) || 0,
        handling_incidental_expenses: parseFloat(formData.handling_incidental_expenses) || 0,
        pme_drive: parseFloat(formData.pme_drive) || 0,
        price_benefits: parseFloat(formData.price_benefits) || 0,
        discounts: parseFloat(formData.discounts) || 0,
        baas: parseFloat(formData.baas) || 0
      };
      
      if (editingPrice) {
        await axios.put(`${BACKEND_URL}/api/masters/vehicle-prices/${editingPrice.id}`, payload, getAuthHeader());
        toast.success('Vehicle price updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/masters/vehicle-prices`, payload, getAuthHeader());
        toast.success('Vehicle price created successfully');
      }
      
      setShowModal(false);
      resetForm();
      fetchPrices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vehicle price');
    }
  };

  const handleDelete = async (priceId) => {
    if (!window.confirm('Are you sure you want to delete this price entry?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/masters/vehicle-prices/${priceId}`, getAuthHeader());
      toast.success('Vehicle price deleted');
      fetchPrices();
    } catch (error) {
      toast.error('Failed to delete vehicle price');
    }
  };

  const viewHistory = async (price) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/masters/vehicle-prices/${price.id}/history`,
        getAuthHeader()
      );
      setPriceHistory(response.data);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error('Failed to fetch price history');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/masters/vehicle-prices/template/download`,
        { ...getAuthHeader(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vehicle_price_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const targetCo = await ensureCompanyId();
    if (!targetCo) { toast.info('Upload cancelled — no target company selected'); e.target.value = ''; return; }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('target_company_id', targetCo);
    
    try {
      toast.loading('Uploading prices...');
      const response = await axios.post(
        `${BACKEND_URL}/api/masters/vehicle-prices/bulk-upload`,
        formData,
        {
          headers: {
            'Authorization': getAuthHeader().headers.Authorization,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.dismiss();
      toast.success(response.data.message);
      fetchPrices();
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
    e.target.value = '';
  };

  const filteredPrices = prices.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (p.brand_name || '').toLowerCase().includes(term) ||
      (p.model || '').toLowerCase().includes(term) ||
      (p.variant || '').toLowerCase().includes(term) ||
      (p.color || '').toLowerCase().includes(term)
    );
  });

  const { cgstAmt, sgstAmt, totalGst, onRoad } = calculateTotals();

  if (loading) {
    return (
      <SalesLayout user={user} onLogout={onLogout} title="Vehicle Price Master">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      </SalesLayout>
    );
  }

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Vehicle Price Master">
      {guardModal}
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Header */}
        <div className="bg-white rounded-2xl border shadow-sm p-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <IndianRupee className="w-7 h-7 text-teal-600" />
                Vehicle Price Master
              </h1>
              <p className="text-gray-500 mt-1">Manage vehicle pricing with GST calculations and price history</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                Download Template
              </button>
              <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 text-sm font-medium cursor-pointer">
                <Upload className="w-4 h-4" />
                Bulk Upload
                <input type="file" accept=".xlsx,.xls" onChange={handleBulkUpload} className="hidden" />
              </label>
              <button
                onClick={openCreateModal}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl shadow-md hover:bg-teal-700 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Price
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl border p-4">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by brand, model, variant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Price List */}
        {filteredPrices.length === 0 ? (
          <div className="bg-white rounded-2xl border p-12 text-center">
            <IndianRupee className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700">No Vehicle Prices</h3>
            <p className="text-gray-500 mt-1">Add your first vehicle price to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredPrices.map(price => (
              <div key={price.id} className="bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <div className="p-4 bg-gradient-to-r from-teal-50 to-emerald-50 border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-gray-800">{price.brand_name}</h3>
                      <p className="text-sm text-gray-600">{price.model} - {price.variant}</p>
                      {price.color && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-white rounded text-xs text-gray-600">{price.color}</span>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${price.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {price.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Ex-Showroom</span>
                    <span className="font-medium">{formatCurrency(price.ex_showroom_price)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">GST ({price.cgst_percent + price.sgst_percent}%)</span>
                    <span className="font-medium">{formatCurrency(price.total_gst)}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t font-semibold">
                    <span className="text-teal-700">On-Road Price</span>
                    <span className="text-teal-700">{formatCurrency(price.on_road_price)}</span>
                  </div>
                  {price.effective_from && (
                    <div className="text-xs text-gray-400 pt-2">
                      Effective: {price.effective_from}
                    </div>
                  )}
                </div>
                <div className="p-3 bg-gray-50 border-t flex gap-2">
                  <button
                    onClick={() => viewHistory(price)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100"
                  >
                    <History className="w-3 h-3" /> History
                  </button>
                  <button
                    onClick={() => openEditModal(price)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-teal-50 text-teal-700 rounded-lg text-xs font-medium hover:bg-teal-100"
                  >
                    <Edit2 className="w-3 h-3" /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(price.id)}
                    className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 my-4">
              <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-t-2xl">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Car className="w-5 h-5" />
                  {editingPrice ? 'Edit Vehicle Price' : 'Add Vehicle Price'}
                </h2>
                <button onClick={() => { setShowModal(false); resetForm(); }} className="p-2 hover:bg-white/20 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 max-h-[70vh] overflow-y-auto">
                {/* Vehicle Selection */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Car className="w-4 h-4 text-teal-600" /> Vehicle Details
                  </h3>
                  {mastersLoading ? (
                    <div className="text-center py-4 text-gray-500">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500 mx-auto mb-2"></div>
                      Loading vehicle masters...
                    </div>
                  ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Brand *</label>
                      <select
                        value={formData.brand_name}
                        onChange={(e) => handleInputChange('brand_name', e.target.value)}
                        className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                        required
                      >
                        <option value="">Select Brand</option>
                        {brands.map(b => <option key={b.id || b.name} value={b.name}>{b.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Model *</label>
                      <select
                        value={formData.model}
                        onChange={(e) => handleInputChange('model', e.target.value)}
                        className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                        required
                      >
                        <option value="">Select Model</option>
                        {models.map(m => (
                          <option key={m.id || m.name} value={m.name}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Variant *</label>
                      <select
                        value={formData.variant}
                        onChange={(e) => handleInputChange('variant', e.target.value)}
                        className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                        required
                      >
                        <option value="">Select Variant</option>
                        {variants.map(v => (
                          <option key={v.id || v.name} value={v.name}>{v.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
                      <select
                        value={formData.color}
                        onChange={(e) => handleInputChange('color', e.target.value)}
                        className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">Select Color</option>
                        {colors.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
                      </select>
                    </div>
                  </div>
                  )}
                </div>

                {/* Pricing */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-teal-600" /> Pricing Details
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Ex-Showroom Price *</label>
                      <input
                        type="number"
                        value={formData.ex_showroom_price}
                        onChange={(e) => handleInputChange('ex_showroom_price', e.target.value)}
                        className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                        placeholder="150000"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">CGST %</label>
                      <div className="relative">
                        <input
                          type="number"
                          value={formData.cgst_percent}
                          onChange={(e) => handleInputChange('cgst_percent', e.target.value)}
                          className="w-full p-2.5 pr-8 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                          step="0.01"
                        />
                        <Percent className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">= {formatCurrency(cgstAmt)}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">SGST %</label>
                      <div className="relative">
                        <input
                          type="number"
                          value={formData.sgst_percent}
                          onChange={(e) => handleInputChange('sgst_percent', e.target.value)}
                          className="w-full p-2.5 pr-8 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                          step="0.01"
                        />
                        <Percent className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">= {formatCurrency(sgstAmt)}</p>
                    </div>
                    <div className="bg-teal-50 rounded-lg p-3">
                      <label className="block text-xs font-medium text-teal-700">Total GST</label>
                      <p className="text-lg font-bold text-teal-700">{formatCurrency(totalGst)}</p>
                    </div>
                  </div>
                </div>

                {/* Additional Charges */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Additional Charges (Variable per Vehicle)</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { key: 'comprehensive_insurance', label: 'Comprehensive Insurance' },
                      { key: 'b2b', label: 'B2B' },
                      { key: 'bpp_battery_protection', label: 'BPP (Battery Protection)' },
                      { key: 'rsa_road_side_assistance', label: 'RSA (Road Side Assistance)' },
                      { key: 'mandatory_accessories', label: 'Mandatory Accessories' },
                      { key: 'registration_expenses', label: 'Registration Expenses' },
                      { key: 'handling_incidental_expenses', label: 'Handling & Incidental' },
                      { key: 'pme_drive', label: 'PME Drive' },
                      { key: 'baas', label: 'BAAS' },
                    ].map(field => (
                      <div key={field.key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{field.label}</label>
                        <input
                          type="number"
                          value={formData[field.key]}
                          onChange={(e) => handleInputChange(field.key, e.target.value)}
                          className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                          placeholder="0"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Deductions */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Deductions</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Price Benefits</label>
                      <input
                        type="number"
                        value={formData.price_benefits}
                        onChange={(e) => handleInputChange('price_benefits', e.target.value)}
                        className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Discounts</label>
                      <input
                        type="number"
                        value={formData.discounts}
                        onChange={(e) => handleInputChange('discounts', e.target.value)}
                        className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Effective Dates */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-teal-600" /> Effective Period
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Effective From</label>
                      <input
                        type="date"
                        value={formData.effective_from}
                        onChange={(e) => handleInputChange('effective_from', e.target.value)}
                        className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Effective To</label>
                      <input
                        type="date"
                        value={formData.effective_to}
                        onChange={(e) => handleInputChange('effective_to', e.target.value)}
                        className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
                      <input
                        type="text"
                        value={formData.remarks}
                        onChange={(e) => handleInputChange('remarks', e.target.value)}
                        className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                        placeholder="e.g., Launch price, Festival offer"
                      />
                    </div>
                  </div>
                </div>

                {/* On-Road Price Summary */}
                <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-xl p-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-teal-100 text-sm">Calculated On-Road Price</p>
                      <p className="text-3xl font-bold">{formatCurrency(onRoad)}</p>
                    </div>
                    <IndianRupee className="w-12 h-12 text-white/30" />
                  </div>
                </div>
              </form>

              <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-2xl">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 bg-teal-600 text-white rounded-xl shadow-md hover:bg-teal-700"
                >
                  {editingPrice ? 'Update Price' : 'Save Price'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* History Modal */}
        {showHistoryModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-600" />
                  Price History
                </h2>
                <button onClick={() => setShowHistoryModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 max-h-96 overflow-y-auto">
                {priceHistory.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No price history available</p>
                ) : (
                  <div className="space-y-3">
                    {priceHistory.map((h, idx) => (
                      <div key={h.id || idx} className="border rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{h.brand_name} {h.model} {h.variant}</p>
                            <p className="text-sm text-gray-500">{h.color}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-teal-700">{formatCurrency(h.on_road_price)}</p>
                            <p className="text-xs text-gray-400">Ex: {formatCurrency(h.ex_showroom_price)}</p>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-400 flex gap-4">
                          <span>From: {h.effective_from || 'N/A'}</span>
                          <span>To: {h.effective_to || 'Current'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </SalesLayout>
  );
}

export default VehiclePriceMaster;
